"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import MegaMenuPanel from "@modules/layout/components/mega-menu/mega-menu-panel"
import { NavigationItem } from "types/navigation"

export default function MegaMenuNav({ items }: { items: NavigationItem[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navRef = useRef<HTMLDivElement>(null)

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
  }, [])

  const handleItemEnter = useCallback(
    (index: number) => {
      clearCloseTimeout()
      setActiveIndex(index)
    },
    [clearCloseTimeout]
  )

  const handleNavLeave = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setActiveIndex(null)
    }, 150)
  }, [])

  const handlePanelEnter = useCallback(() => {
    clearCloseTimeout()
  }, [clearCloseTimeout])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveIndex(null)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const activeItem = activeIndex !== null ? items[activeIndex] : null
  const showPanel = activeItem && activeItem.children.length > 0

  return (
    <div ref={navRef} className="relative" onMouseLeave={handleNavLeave}>
      <div className="max-w-screen-2xl mx-auto px-4">
        <ul
          className="flex justify-center items-center gap-0 text-[13px] font-medium"
          role="menubar"
        >
          {items.map((item, index) => {
            const hasChildren = item.children.length > 0
            const isActive = activeIndex === index

            const className = `whitespace-nowrap transition-colors px-3 py-3 inline-flex items-center border-b-2 ${
              isActive
                ? "border-white text-white"
                : "border-transparent"
            } ${
              item.highlight
                ? "text-brand-accent font-bold hover:text-red-400"
                : "text-white/90 hover:text-white"
            }`

            return (
              <li key={item.id} role="none">
                {hasChildren ? (
                  <button
                    role="menuitem"
                    aria-haspopup="true"
                    aria-expanded={isActive}
                    className={className}
                    onMouseEnter={() => handleItemEnter(index)}
                    onFocus={() => handleItemEnter(index)}
                  >
                    {item.label}
                  </button>
                ) : (
                  <LocalizedClientLink
                    role="menuitem"
                    href={item.url}
                    className={className}
                    onMouseEnter={() => handleItemEnter(index)}
                  >
                    {item.label}
                  </LocalizedClientLink>
                )}
              </li>
            )
          })}
        </ul>
      </div>

      {showPanel && (
        <div onMouseEnter={handlePanelEnter} onMouseLeave={handleNavLeave}>
          <MegaMenuPanel item={activeItem} />
        </div>
      )}
    </div>
  )
}
